/**
 * =============================================================================
 * USE STYLE INJECTION HOOK
 * =============================================================================
 *
 * Injects CSS styles into the document head, ensuring only one style tag
 * exists per unique ID regardless of how many component instances render.
 *
 * ARCHITECTURE REFERENCE: PHASE_0C_SPEC.md §2 (0C-9)
 *
 * Features:
 * - Injects styles only once per unique ID
 * - Reference counting for multiple component instances
 * - Cleans up styles when last instance unmounts
 * - Prevents duplicate style tags in the DOM
 * - Uses useLayoutEffect to prevent FOUC (Flash of Unstyled Content)
 * - SSR-safe with typeof document guard
 *
 * =============================================================================
 */

import { useEffect, useLayoutEffect } from 'react';
import { logger } from '../lib/logger';

/**
 * Isomorphic layout effect that uses useLayoutEffect in browser
 * and useEffect in SSR environments to avoid React warnings.
 *
 * @internal
 */
const useIsomorphicLayoutEffect =
  typeof window !== 'undefined' ? useLayoutEffect : useEffect;

/**
 * Track the number of component instances using each style ID.
 * This enables cleanup only when the last instance unmounts.
 *
 * @internal Module-level state for reference counting
 */
const styleRefCounts = new Map<string, number>();

/**
 * Injects CSS styles into the document head with deduplication.
 *
 * This hook ensures that:
 * 1. Only one `<style>` tag is created per unique ID
 * 2. Multiple component instances share the same style tag
 * 3. The style tag is removed only when ALL instances unmount
 *
 * Uses `useLayoutEffect` to inject styles synchronously before browser paint,
 * preventing Flash of Unstyled Content (FOUC).
 *
 * @param styles - The CSS styles to inject (should be a stable constant)
 * @param id - Unique identifier for the style tag (should be a stable constant)
 *
 * @example
 * ```tsx
 * const imageUploaderStyles = `.fmp-uploader { ... }`;
 *
 * function ImageUploader() {
 *   useStyleInjection(imageUploaderStyles, 'fmp-image-uploader-styles');
 *   return <div className="fmp-image-uploader">...</div>;
 * }
 * ```
 *
 * @note The `id` should be unique across the application to prevent conflicts.
 *       Convention: use component name prefixed with 'fmp-' and suffixed with '-styles'
 *       Example: 'fmp-image-uploader-styles', 'fmp-question-form-styles'
 *
 * @warning Both `styles` and `id` parameters should be stable constants, not dynamic values.
 *          Changing these values between renders may cause unexpected behavior.
 */
export function useStyleInjection(styles: string, id: string): void {
  useIsomorphicLayoutEffect(() => {
    // SSR guard: skip if document is not available
    if (typeof document === 'undefined') return;

    // Increment reference count for this style ID
    const currentCount = styleRefCounts.get(id) || 0;
    styleRefCounts.set(id, currentCount + 1);

    // Development mode: warn if same ID is used with different styles
    if (import.meta.env.DEV) {
      const existing = document.getElementById(id) as HTMLStyleElement | null;
      if (existing && existing.textContent !== styles) {
        logger.warn('STYLE_ID_CONFLICT', {
          styleId: id,
          hint: 'First-registered styles will be used. Consider using unique IDs per style variant.',
        });
      }
    }

    // Only inject styles if this is the first instance
    if (!document.getElementById(id)) {
      const style = document.createElement('style');
      style.id = id;
      style.textContent = styles;
      document.head.appendChild(style);
    }

    // Cleanup: decrement ref count, remove style tag if last instance
    return () => {
      // SSR guard for cleanup as well
      if (typeof document === 'undefined') return;

      const count = styleRefCounts.get(id) || 0;
      if (count <= 1) {
        // Last instance - remove the style tag and clean up tracking
        styleRefCounts.delete(id);
        document.getElementById(id)?.remove();
      } else {
        // Other instances still exist - just decrement count
        styleRefCounts.set(id, count - 1);
      }
    };
  }, [styles, id]);
}

/**
 * Get the current reference count for a style ID.
 *
 * @internal **TEST UTILITY ONLY** - Do not use in production code.
 * This function exposes internal implementation details and is
 * subject to change without notice. It is exported solely for
 * testing purposes to verify reference counting behavior.
 *
 * @param id - The style ID to check
 * @returns The current reference count, or 0 if not tracked
 */
export function getStyleRefCount(id: string): number {
  return styleRefCounts.get(id) || 0;
}

/**
 * Clear all style reference counts.
 *
 * @internal **TEST UTILITY ONLY** - Do not use in production code.
 * This function resets internal state and should only be called
 * in test cleanup (beforeEach/afterEach). Using this in production
 * will cause styles to be incorrectly cleaned up.
 */
export function clearStyleRefCounts(): void {
  styleRefCounts.clear();
}
